import { Radio } from 'ui/icons';
import { IHospital } from '../../../types';
import { Directions, Share } from 'ui/mobile-ds/icons';
import { PopoverButton, PopoverPanel, PopoverWrapper } from 'ui/ds/Popover';
import { InputHTMLAttributes, forwardRef, useState } from 'react';
import { ConditionalLink } from '@/components/Common';
import { useAppContext } from '@/context/AppContext';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { usePopper } from 'ui/ds/Popover/Popover';
import { useDebouncedCallback } from 'use-debounce';
import { track } from '@/utils';
import { TRACKER_EVENTS } from '@/constants';

type IProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'title' | 'onSelect'
> & {
  mode: 'view' | 'select';
  selected?: boolean;
  onSelect?: (
    hospital: Partial<Omit<IHospital, 'distance'>> & {
      distance?: string;
      mapsUrl?: string;
    },
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  hospital: Partial<Omit<IHospital, 'distance'>> & {
    distance?: string;
    mapsUrl?: string;
  };
  getTrackingStats?: () => Record<string, unknown>;
  query: string;
};

interface IHighlightNameProps {
  name: string | undefined;
  searchString: string;
}

const HighlightName = ({ name, searchString }: IHighlightNameProps) => {
  if (!searchString) return <span>{name}</span>;

  const regex = new RegExp(`(${searchString})`, 'gi'); // Create a case-insensitive regex for the search string
  const parts = name?.split(regex); // Split the name based on the search string

  return (
    <span>
      {parts?.map((part, index) =>
        part.toLowerCase() === searchString.toLowerCase() ? (
          <span key={index} className="font-bold">
            {part}
          </span> // Highlight the matched part
        ) : (
          <span key={index}>{part}</span> // Display the rest of the name normally
        )
      )}
    </span>
  );
};

const HospitalListCard = forwardRef<HTMLInputElement, IProps>(
  (props: IProps, ref) => {
    const {
      mode = 'view',
      hospital,
      selected,
      onSelect,
      getTrackingStats,
      className,
      query,
      ...rest
    } = props;

    const { isWebView } = useAppContext();

    const [referenceElement, setReferenceElement] =
      useState<HTMLButtonElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
      null
    );
    const { styles, attributes } = usePopper(referenceElement, popperElement);

    const shareObject = {
      text: `${hospital.name}\n${hospital.address}\n\n${
        hospital.mapsUrl ? `Directions: ${hospital.mapsUrl}` : ''
      }\n${
        (hospital.phone?.length ?? 0) > 0
          ? `Phone: ${hospital?.phone?.join(', ')}`
          : ''
      }\n${
        (hospital?.email ?? '').length > 0 ? `Email: ${hospital.email}` : ''
      }`,
    };

    const trackMapClick = () => {
      track({
        event: TRACKER_EVENTS.NH_MAP_CTA_CLICKED,
        data: getTrackingStats?.() ?? {},
      });
    };

    const onMapClick: React.MouseEventHandler<HTMLElement> = (event) => {
      event.preventDefault();
      trackMapClick();
      if (!hospital.mapsUrl) {
        toast('No map link available', {
          id: 'no-map-link-available',
          style: {
            marginBottom: 'calc(84px + 6rem)',
          },
        });
      }
      if (isWebView && hospital.mapsUrl) {
        window?.NativeBridge?.openMapLink?.(hospital.mapsUrl);
      } else if (!isWebView && hospital.mapsUrl) {
        window.open(hospital.mapsUrl, '_blank');
      }
    };

    const trackShare = () => {
      track({
        event: TRACKER_EVENTS.NH_SHARE_CTA_CLICKED,
        data: getTrackingStats?.() ?? {},
      });
    };

    const onShareText: React.MouseEventHandler<HTMLElement> = (event) => {
      event.preventDefault();
      trackShare();
      if (isWebView) {
        window?.NativeBridge?.onShareText?.(shareObject.text);
      }
    };

    const debouncedShare = useDebouncedCallback(onShareText, 500);

    const onCopyText = (
      event: React.MouseEvent<HTMLElement, MouseEvent>,
      close: (
        focusableElement?:
          | HTMLElement
          | React.MutableRefObject<HTMLElement | null>
      ) => void
    ) => {
      event.preventDefault();
      navigator.clipboard.writeText(shareObject.text);
      close(referenceElement ?? undefined);
      toast('Hospital details copied to clipboard', {
        style: {
          marginBottom: 'calc(84px + 6rem)',
        },
      });
    };

    const onEmailClick = (
      event: React.MouseEvent<HTMLElement, MouseEvent>,
      close: (
        focusableElement?:
          | HTMLElement
          | React.MutableRefObject<HTMLElement | null>
      ) => void
    ) => {
      event.preventDefault();
      close(referenceElement ?? undefined);
      window.open(
        `mailto:?body=${encodeURIComponent(shareObject.text)}`,
        '_parent'
      );
    };

    const Card = (
      <div className={className}>
        <h1 className="text-mobile-text-main font-medium leading-[130%] capitalize">
          <HighlightName name={hospital.name} searchString={query} />
        </h1>
        <p className="mt-2 text-mobile-text-hierarchy text-sm leading-[160%] capitalize">
          {hospital.address}
        </p>
        {hospital.distance ? (
          <p className="mt-2 text-mobile-text-hierarchy text-sm leading-[160%]">
            {hospital.distance}
          </p>
        ) : null}
        <div className="mt-4 flex items-start gap-4">
          {/* maps url */}
          <ConditionalLink
            external
            href={isWebView ? null : hospital.mapsUrl}
            className={clsx({
              'flex items-center': true,
              'opacity-50': !hospital.mapsUrl,
            })}
            asButton
            onClick={onMapClick}
          >
            <Directions />
          </ConditionalLink>
          {/* share details */}
          {isWebView ? (
            <ConditionalLink
              className={clsx({
                'flex items-center': true,
              })}
              onClick={debouncedShare}
              asButton
            >
              <Share />
            </ConditionalLink>
          ) : (
            <PopoverWrapper>
              <PopoverButton
                ref={(elem) => setReferenceElement(elem)}
                className="flex items-center"
                onClick={trackShare}
              >
                <Share />
              </PopoverButton>
              <PopoverPanel
                ref={setPopperElement}
                style={styles.arrow}
                {...attributes.popper}
              >
                {({ close }) => {
                  return (
                    <div className="py-2 bg-white shadow-card w-24 flex flex-col text-left rounded-lg gap-2 fixed">
                      <button
                        className="text-left px-4 py-1 hover:bg-gray-100"
                        onClick={(event) => onCopyText(event, close)}
                      >
                        Copy
                      </button>
                      <button
                        className="text-left w-full"
                        onClick={(event) => onEmailClick(event, close)}
                      >
                        <a
                          target="_parent"
                          href={`mailto:?body=${encodeURIComponent(
                            shareObject.text
                          )}`}
                          className="text-left px-4 py-1 hover:bg-gray-100 w-full flex"
                          tabIndex={-1}
                        >
                          Email
                        </a>
                      </button>
                    </div>
                  );
                }}
              </PopoverPanel>
            </PopoverWrapper>
          )}
        </div>
      </div>
    );

    if (mode === 'view') {
      return Card;
    }

    return (
      <div className="flex items-center">
        <label
          className="w-full flex items-center cursor-pointer"
          htmlFor={hospital.id}
        >
          {Card}
          <input
            ref={ref}
            id={hospital.id}
            name="hospital"
            className="accent-primary-500 ml-auto h-[1px] w-[1px] invisible"
            type="radio"
            {...rest}
            onChange={(event) => {
              rest.onChange?.(event);
              onSelect?.(hospital, event);
            }}
          />
          <Radio
            isChecked={rest.checked || selected}
            style={{
              flex: '0 0 auto',
            }}
          />
        </label>
      </div>
    );
  }
);

export default HospitalListCard;
